/****************
 * THE SPEED RUN'S BRAIN, ONE FOR BOTH APPS (twin of mobile-app/src/lib).
 *
 * Which step the run is on, what glows, and when it goes on. The screens
 * (web components/engagement/SpeedRunGuide.tsx, phone
 * components/tour/SpeedRunGuide.tsx) only draw it.
 *
 * Nothing here navigates (Guillaume 11-01: "no automatic navigation should
 * ever happen"). When the person is not on the step's page, the step's card
 * says so and the menu button of that page glows; they press it.
 *
 * Kept in this browser (localStorage) so a reload, or the run's pages
 * mounting one after another, picks up where it was. Signals are remembered
 * once seen: a backtest that finished before its step is reached still counts.
 ****************/
import { RUN_PRO_STEP, SPEED_RUN_PATH, type RunPage, type RunStep } from './speedRunPath.core';

const STEP_KEY = 'copyfi.speedRun.step';
const SEEN_KEY = 'copyfi.speedRun.seen';

/** The menu button that leads to each page (data-tour on the web, testID on the phone). */
export const NAV_FOR: Record<RunPage, string | null> = {
  dashboard: 'nav-dashboard',
  traders: 'nav-traders',
  trader: 'nav-traders',
  strategies: 'nav-strategy',
  copies: 'nav-sessions',
  learn: 'nav-learn',
  backtest: 'nav-sessions',
  any: null,
};

function store(): Storage | null {
  try { return typeof localStorage === 'undefined' ? null : localStorage; } catch { return null; }
}

export function runStepIndex(): number {
  const n = Number(store()?.getItem(STEP_KEY) ?? '0');
  return Number.isFinite(n) && n >= 0 ? Math.min(n, SPEED_RUN_PATH.length) : 0;
}

export function setRunStepIndex(i: number): void {
  try { store()?.setItem(STEP_KEY, String(Math.max(0, Math.min(i, SPEED_RUN_PATH.length)))); } catch { /* private mode */ }
}

export function seenSignals(): string[] {
  try { return JSON.parse(store()?.getItem(SEEN_KEY) || '[]') as string[]; } catch { return []; }
}

export function rememberSignal(name: string): void {
  try { store()?.setItem(SEEN_KEY, JSON.stringify([...new Set([...seenSignals(), name])])); } catch { /* private mode */ }
}

/** Every run starts at the first step with nothing seen. */
export function resetRunProgress(): void {
  try { store()?.removeItem(STEP_KEY); store()?.removeItem(SEEN_KEY); } catch { /* private mode */ }
}

export function currentStep(): RunStep | null {
  return SPEED_RUN_PATH[runStepIndex()] ?? null;
}

/** The run reached CopyFi Pro (its lock opens from this step on). */
export function runReachedPro(): boolean {
  const pro = SPEED_RUN_PATH.findIndex((s) => s.id === RUN_PRO_STEP);
  return pro >= 0 && runStepIndex() >= pro;
}

/** Where the person is: the pages the screen counts as (the web's My Copies on its Backtest tab is both 'copies' and 'backtest'). */
export type Here = RunPage | RunPage[] | null;
const isAt = (here: Here, p: RunPage) => (Array.isArray(here) ? here.includes(p) : here === p);

/** Is the person on the step's page? */
export function onStepPage(step: RunStep, here: Here): boolean {
  return step.page === 'any' || isAt(here, step.page);
}

export interface RunView {
  step: RunStep;
  index: number;
  total: number;
  /** The controls that may glow, in order: the first on screen does. */
  glow: string[];
  /** Elsewhere: the page to go to, by its glowing menu button. */
  goTo: RunPage | null;
  /** Next on the card (a step that waits for an action has none). */
  next: boolean;
}

/** What the screen shows for the person on `page`. null when the run is over. */
export function runView(page: Here): RunView | null {
  const index = runStepIndex();
  const step = SPEED_RUN_PATH[index];
  if (!step) return null;
  const base = { step, index, total: SPEED_RUN_PATH.length };
  if (!onStepPage(step, page)) {
    const nav = NAV_FOR[step.page];
    // The backtests live inside My Copies (New copy, then its Backtest tab, then the
    // list): already in My Copies, the way there glows and the menu line is not shown
    // (11-02 audit: "press My Copies" while on My Copies).
    if (step.page === 'backtest' && isAt(page, 'copies')) {
      return { ...base, glow: ['bt-history', 'ct-tab-backtest', 'ct-new'], goTo: null, next: false };
    }
    return { ...base, glow: nav ? [nav] : [], goTo: step.page, next: false };
  }
  const glow = step.target === null ? [] : Array.isArray(step.target) ? step.target : [step.target];
  return { ...base, glow, goTo: null, next: !step.doneWhen };
}

/*
 * Moves on while the current step is done: its page reached, or its signal
 * seen (now or earlier). Several at once when they are (a backtest done long
 * ago). Returns the new index.
 */
export function advance(page: Here): number {
  let i = runStepIndex();
  const seen = seenSignals();
  for (;;) {
    const s = SPEED_RUN_PATH[i];
    if (!s || !s.doneWhen) break;
    const done = (s.doneWhen.page && isAt(page, s.doneWhen.page)) || (s.doneWhen.signal && seen.includes(s.doneWhen.signal));
    if (!done) break;
    i += 1;
  }
  if (i !== runStepIndex()) setRunStepIndex(i);
  return i;
}

/** The card's Next (only on a step that waits for nothing). */
export function nextStep(): number {
  const i = runStepIndex() + 1;
  setRunStepIndex(i);
  return i;
}

/** A No that goes back (RunStep.ask.back): to that step, by its id. */
export function jumpTo(id: string): number {
  const i = SPEED_RUN_PATH.findIndex((x) => x.id === id);
  if (i >= 0) setRunStepIndex(i);
  return runStepIndex();
}
